import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { Delete } from "@mui/icons-material";
import { Button, Chip, Snackbar, styled, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import Alert from "../common/Alert";
import ConfirmationDialog from "../common/ConfirmationDialog";
import Error from "../common/Error";
import Loading from "../common/Loading";
import { pages } from "../pages/pages";
import theme from "../styles/theme";
import { LocationSettingState } from "../types";

export const deleteSettingMutation = gql`
    mutation DeleteSetting($id: ID!) {
        deleteSetting(id: $id)
    }
`;

const Setting: React.FunctionComponent = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [deleteMutation] = useMutation(deleteSettingMutation);
    const [isLoading, setIsLoading] = React.useState(false);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [open, setOpen] = React.useState(false);

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }

        setOpen(false);
    };

    const { setting } = (location.state as LocationSettingState) ?? { setting: undefined };

    const handleDelete = (value?: boolean) => {
        if (value && setting) {
            setIsLoading(true);
            deleteMutation({
                variables: {
                    id: setting.id,
                },
                refetchQueries: ["GetCountedSettings"],
            })
                .then(() => {
                    setIsLoading(false);
                    navigate(pages.profile, { state: { deletedSetting: true } });
                })
                .catch(() => {
                    setIsLoading(false);
                    setOpen(true);
                });
        }
        setDialogOpen(false);
    };

    return (
        <Container>
            {isLoading && <Loading />}
            {setting ? (
                <>
                    <Typography textAlign="center" variant="h5">
                        {setting.name}
                    </Typography>
                    <Typography style={{ marginTop: 20 }}>
                        <b>{t("form_discreet")}:</b> {setting.discreet ? t("yes") : t("no")}
                    </Typography>
                    <Typography style={{ marginTop: 10 }}>
                        <b>{t("form_favourites")}:</b> {setting.favourite ? t("yes") : t("no")}
                    </Typography>
                    <Typography fontWeight="700" style={{ marginTop: 20, marginBottom: 10 }}>
                        {t("form_exercises")}:
                    </Typography>
                    <div>
                        {setting.categories.map((category) => (
                            <Chip
                                key={category}
                                style={{
                                    marginRight: 8,
                                    marginBottom: 8,
                                    backgroundColor: theme.colors[category],
                                    color: theme.palette.primary.contrastText,
                                }}
                                label={t(category)}
                            />
                        ))}
                    </div>
                    <ButtonContainer>
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => {
                                setDialogOpen(true);
                            }}
                        >
                            {t("delete_setting")}
                        </Button>
                    </ButtonContainer>
                </>
            ) : (
                <Error text={t("error")} />
            )}
            <ConfirmationDialog open={dialogOpen} onClose={handleDelete} title="delete_setting">
                {t("delete_setting_content")}
            </ConfirmationDialog>
            <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
                <Alert onClose={handleClose} severity="error" sx={{ width: "100%" }}>
                    {t("error")}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Setting;

const Container = styled("div")`
    padding: 30px;
`;

const ButtonContainer = styled("div")`
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 20px;
`;
